import { SpecificationProp } from 'features/car-details-card/types'
import { createMediaStyles } from 'lib/styled-helpers'
import { usePriceFormatter } from 'lib/use-price-formatter'
import React, { FC } from 'react'
import { useCarDetailsCardConfig } from 'stages/car-details'
import styled, { useTheme } from 'styled-components'
import { Collapse } from 'ui/collapse'
import { DottedPropertyValue } from 'ui/dotted-property-value'
import { Cell, Row } from 'ui/grid'
import { Tab, Tabs } from 'ui/tabs'

import { getProjectName } from '../../../lib/get-project-name'

const PropertyListComponent = ({
  props,
  remainderValue
}: {
  props: SpecificationProp[]
  remainderValue: 0 | 1 | null
}) => {
  return (
    <PropertyList>
      {props.map((prop, index) => {
        return index % 2 === remainderValue || remainderValue === null ? (
          <PropertyItem key={index}>
            <DottedPropertyValue
              property={<PropertyText>{prop.title}</PropertyText>}
              value={<Value>{prop.definition}</Value>}
            ></DottedPropertyValue>
            {/* <Property>
              <PropertyText>{prop.title}</PropertyText>
            </Property>
            <Value>{prop.definition}</Value> */}
          </PropertyItem>
        ) : null
      })}
    </PropertyList>
  )
}

export type Props = {
  additionalEquipmentCost: number | null
}

export const DetailsCardTabs: FC<Props> = ({ additionalEquipmentCost }) => {
  const { carViewSettings, tabs } = useCarDetailsCardConfig()
  const formatPrice = usePriceFormatter()
  const theme = useTheme()
  const isCompactView = ['phone', 'phablet'].includes(theme.activeBreakpoint)

  return (
    <Tabs tabsLength={tabs.length}>
      {tabs.map(({ title, content }, index: number) => {
        return (
          <Tab title={title} key={index}>
            {content.element === 'options' ? (
              <>
                {content.value.sections.items.map((item, index) => {
                  return (
                    <ItemWrapper key={index}>
                      <Collapse title={item.title} newVersion={true} isArrows={false}>
                        <OptionList newVersion={true}>
                          {item.content.options.map((option, index) => {
                            return (
                              <OptionItem key={index} newVersion={true}>
                                {option}
                              </OptionItem>
                            )
                          })}
                        </OptionList>
                      </Collapse>
                    </ItemWrapper>
                  )
                })}

                {content.value.representsAdditionalEquipment && additionalEquipmentCost && (
                  <EquipmentPrice>
                    <DottedPropertyValue
                      property={carViewSettings?.i18n?.additionalEquipmentPrice}
                      value={formatPrice(additionalEquipmentCost)}
                    ></DottedPropertyValue>
                  </EquipmentPrice>
                )}
              </>
            ) : content.element === 'specifications' ? (
              content.value.sections.map((section, index) => {
                return (
                  <>
                    {getProjectName() !== 'generic_v2' && (
                      <SpecificationWrapper key={index}>
                        <Title>{section.title}</Title>
                        {isCompactView ? (
                          <PropertyListComponent props={section.props} remainderValue={null} />
                        ) : (
                          <Row>
                            <Cell size={6}>
                              <Row flexDirection="column">
                                <Cell>
                                  <PropertyListComponent props={section.props} remainderValue={0} />
                                </Cell>
                              </Row>
                            </Cell>
                            <Cell size={6}>
                              <Row flexDirection="column">
                                <Cell>
                                  <PropertyListComponent props={section.props} remainderValue={1} />
                                </Cell>
                              </Row>
                            </Cell>
                          </Row>
                        )}
                      </SpecificationWrapper>
                    )}
                    {getProjectName() === 'generic_v2' && (
                      <ItemWrapper key={index}>
                        <Collapse title={section.title} newVersion={true} isArrows={false}>
                          <OptionList className={'specifications'} newVersion={true}>
                            {section.props.map((option, index) => {
                              return (
                                <OptionItem key={index} newVersion={true}>
                                  <span>{option.title}</span>
                                  <span>{option.definition}</span>
                                </OptionItem>
                              )
                            })}
                          </OptionList>
                        </Collapse>
                      </ItemWrapper>
                    )}
                  </>
                )
              })
            ) : (
              <div>not implemented</div>
            )}
          </Tab>
        )
      })}
    </Tabs>
  )
}

const ItemWrapper = styled.div`
  border-top: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return 'none'
    return `1px solid ${theme.palette.grey[200]}`
  }};
  border-bottom: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return 'none'
    return `1px solid ${theme.palette.grey[200]}`
  }};
  margin-top: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return 0
    return '-1px'
  }};
  margin-bottom: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '4px'
    return 0
  }};

  &:first-child {
    margin-top: ${({ theme }) => theme.spacing[4]};
  }

  @media screen and (max-width: 768px) {
    padding: ${() => {
      if (getProjectName() === 'generic_v2') return '0 20px'
      return 0
    }}
`

const OptionList = styled.ul<{ newVersion: boolean }>`
  margin: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '5px 0 0 0'
    return 0
  }};
  padding-right: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '20px'
    return theme.spacing[4]
  }};
  padding-left: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '20px'
    return theme.spacing[4]
  }};
  padding-bottom: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '10px'
    return theme.spacing[3]
  }};
  padding-top: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '10px'
    return 0
  }};
  width: 100%;
  max-width: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '74%'
    return '100%'
  }};

  @media screen and (max-width: 768px) {
    max-width: none;
  }
  background: ${({ theme, newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return theme.palette.grey[800]
    return 'none'
  }};
  border-radius: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '8px'
    return 0
  }};

  &.specifications {
    background: none;
    padding: 0;

    li {
      justify-content: space-between;
      margin: 0 0 4px 0;
      border-radius: 8px;
      background: ${({ newVersion }) => {
        if (getProjectName() === 'generic_v2' && newVersion) return `rgba(31, 31, 31, 0.5)`
        return 'none'
      }};
      padding: 10px 20px;

      &:last-child {
        margin: 0;
      }

      &:before {
        content: none;
      }
    }
  }
`

const OptionItem = styled.li<{ newVersion: boolean }>`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${() => {
    if (getProjectName() === 'haval') return '15px'
    if (getProjectName() === 'ora') return '13px'
    return '10px'
  }};
  color: ${({ theme, newVersion }) => {
    if (getProjectName() === 'mercedes-benz') return theme.palette.black
    if (getProjectName() === 'ora') return theme.palette.black
    if (getProjectName() === 'generic_v2' && newVersion) return theme.palette.grey[100]
    return theme.palette.grey[500]
  }};
  ${({ theme }) => theme.textStyles.p2};
  line-height: ${() => {
    return getProjectName() === 'haval' ? '1.6rem' : 'normal'
  }};
  font-size: ${({ newVersion }) => {
    if (getProjectName() === 'generic_v2' && newVersion) return '1.4rem'
    if (getProjectName() === 'ora') return '1.6rem'
    return '1.6rem'
  }};
  font-weight: ${() => getProjectName() === 'ora' && '500'};

  @media screen and (max-width: 576px) {
    flex-direction: ${({ newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) {
        return 'column'
      }
      return 'row'
    }};
  }

  &:before {
    content: '';
    display: block;
    flex-shrink: 0;
    width: ${({ newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) return '4px'
      return '7px'
    }};
    height: ${({ newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) return '4px'
      return '7px'
    }};
    margin-top: ${({ newVersion }) => {
      if (getProjectName() === 'haval') return '4px'
      if (getProjectName() === 'generic_v2' && newVersion) return '8px'
      return '6px'
    }};
    margin-right: ${({ newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) return '8px'
      return '12px'
    }};
    margin-left: 3px;
    border-radius: 50%;
    background: ${({ theme, newVersion }) => {
      if (getProjectName() === 'ora') return theme.palette.black
      if (getProjectName() === 'generic_v2' && newVersion) return theme.palette.grey[100]
      return theme.palette.brand.primary
    }};
  }
  
  span:last-child {
    color: ${({ theme, newVersion }) => {
      if (getProjectName() === 'generic_v2' && newVersion) return theme.palette.grey[600]
      return 'inherit'
    }};
    margin-left: 10px;
    text-align: right;
`

const Title = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  ${({ theme }) => theme.textStyles.p1};
  color: ${({ theme }) => theme.palette.grey[900]};
  text-transform: uppercase;
`

const SpecificationWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[10]};
  background: ${({ theme }) => {
    return getProjectName() === 'generic_v2' && theme.palette.grey[800]
  }};

  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding: 0 calc(${theme.grid.containerGutters} / 2);`,
      tablet: `padding-left: ${theme.spacing[1]}; padding-right: ${theme.spacing[0]};`
    }))}
`

const PropertyList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const PropertyItem = styled.li`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const PropertyText = styled.span`
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => {
    if (getProjectName() === 'mercedes-benz') {
      return theme.palette.black
    }
    return theme.palette.grey[500]
  }};
`

const Value = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => theme.palette.grey[900]};
`

const EquipmentPrice = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
  color: ${({ theme }) => theme.palette.grey[900]};
  ${({ theme }) => theme.textStyles.p1};
`
